import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Menu, Zap } from 'lucide-react';
import Sidebar from './Sidebar';
import CommandPalette from '../CommandPalette';
import { useSignalR } from '../../hooks/useSignalR';

const EXACT_TITLES: Record<string, string> = {
  '/dashboard':                  'Dashboard',
  '/tasks':                      'Tasks',
  '/tasks/new':                  'New Task',
  '/check-in':                   'Check In',
  '/check-in/history':           'Check-in History',
  '/check-in/status':            'Check-in Status',
  '/projects':                   'Projects',
  '/sprints':                    'Sprints',
  '/notifications':              'Notifications',
  '/notifications/preferences':  'Notification Preferences',
  '/engineers':                  'Engineers',
  '/escalations':                'Escalations',
  '/pulse':                      'Pulse',
  '/feedback':                   'Feedback Inbox',
  '/feedback/new':               'Submit Feedback',
  '/reports/leadership':         'Leadership Report',
  '/admin/users':                'Users',
  '/admin/teams':                'Teams',
  '/admin/thresholds':           'Thresholds',
  '/admin/audit-log':            'Audit Log',
};

function resolveTitle(pathname: string): string {
  if (EXACT_TITLES[pathname]) return EXACT_TITLES[pathname];
  if (pathname.startsWith('/tasks/'))     return 'Task';
  if (pathname.startsWith('/projects/'))  return 'Project';
  if (pathname.startsWith('/sprints/'))   return 'Sprint';
  if (pathname.startsWith('/engineers/')) return 'Engineer';
  return '';
}

export default function AppShell() {
  useSignalR();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    const page = resolveTitle(location.pathname);
    document.title = page ? `${page} — Cadence` : 'Cadence';
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onOpenPalette={() => { setSidebarOpen(false); setPaletteOpen(true); }}
      />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Open navigation"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary">
              <Zap className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="text-sm font-bold tracking-tight">Cadence</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6" id="main-content">
          <div key={location.pathname} className="page-transition h-full">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
